import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import DrupalAccessTokenApi from "../../../js/csf/DrupalAccessTokenApi";
import { startLoadingSpinner, stopLoadingSpinner } from '../../csf-ufc-pivotal/includes/utils';
import PivotalSearchApi from "../../../js/csf/pivotal/PivotalSearchApi";
import moment from 'moment';
import "moment-timezone";
import { FournisseurRecognitionRequestStepOneBlock } from "./csf-fournisseur-recognition-request-step-1";
import { FournisseurRecognitionRequestStepTwoBlock } from "./csf-fournisseur-recognition-request-step-2";
import { FournisseurRecognitionRequestStepThreeBlock } from "./csf-fournisseur-recognition-request-step-3";
import { FournisseurRecognitionRequestStepFourBlock } from "./csf-fournisseur-recognition-request-step-4";
import PivotalFormApi from "../../../js/csf/pivotal/PivotalFormApi";
import PropTypes from "prop-types";
import { FournisseurRecognitionRequestStepFiveBlock } from './csf-fournisseur-recognition-request-step-5';
import { FournisseurRecognitionRequestStepSixBlock } from './csf-fournisseur-recognition-request-step-6';
import { FournisseurRecognitionRequestStepFourConfirmBlock } from './csf-fournisseur-recognition-request-step-4-confirmation';
import { FournisseurRecognitionRequestStepPaymentBlock } from './csf-fournisseur-recognition-request-step-payment';
import { FournisseurRecognitionRequestStepPaymentConfirmationBlock } from './csf-fournisseur-recognition-request-step-payment-confirmation';
import { getQueryParams } from "../includes/utils";

export function FournisseurRecognitionRequestBlock({ apiUrl, apiEnv, urlActivityDetail, urlHome, urlRequestRecognition, monerisHostUrl, monerisHostId }) {

  const [accessToken, setAccessToken] = useState('');
  const [user, setUser] = useState({});
  const [form, setForm] = useState({});
  const [originalEndDate, setOriginalEndDate] = useState(null);
  const [formStep4, setFormStep4] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [role, setRole] = useState(1);
  const [isErrorMoneris, setIsErrorMoneris] = useState(false);
  const [trainingId, setTrainingId] = useState(''); // or null
  const [activityCycleId, setActivityCycleId] = useState('');
  const [refreshForm, setRefreshForm] = useState(false);
  const [refreshFormStep4, setRefreshFormStep4] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [company, setCompany] = useState([]);
  const [contact, setContact] = useState({});
  const [fistTimeNewRecognition, setFistTimeNewRecognition] = useState(true);
  const [hasPreviousRenewalEndDate, setHasPreviousRenewalEndDate] = useState(false);

  // either formdata obtained from API, or is a totally new request
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  /*
  * On components mounts fetch the user access token only once.
  * */
  useEffect(() => {
    startLoadingSpinner();
    const params = getQueryParams(window.location.search);
    if (params && params.activity) {
      setTrainingId(params.activity);
    }
    fetchAccessToken();
  }, []);

  /*
  * Add a event listener for the tokenization
  * */
  useEffect(() => {
    if (currentStep === 8) {
      window.addEventListener("message", respMsgMoneris, false);
    }
    return () => {
      window.removeEventListener("message", respMsgMoneris, false);
    };
  }, [form]);

  /*
  * When the accessToken is fetched and state is updates, get the connected user
  * */
  useEffect(() => {
    if (accessToken) {
      getConnectedUser();
      if (trainingId !== '' && trainingId !== null) {
        fetchFormFromRecordId();
      } else {
        fetchFormFromLocalStorage();
        setInitialLoadComplete(true);
      }
    }
  }, [accessToken]);

  /*
* When the accessToken is fetched and state is updates, get the connected user
* */
  useEffect(() => {
    if (refreshForm === true) {
      setRefreshForm(false)
      fetchFormFromRecordId();
    }
  }, [refreshForm]);

  useEffect(() => {
    if (refreshFormStep4 === true) {
      setRefreshFormStep4(false);
      fetchFormStep4FromActivityCycleId();
    }
  }, [refreshFormStep4]);

  useEffect(() => {
    if (companyId !== '') {
      getCompanyProfileInfo();
    }
  }, [companyId]);

  /*
  * Method to call drupal api to get the access token of the session.
  * */
  function fetchAccessToken() {
    const drupalAccessTokenApi = new DrupalAccessTokenApi();
    drupalAccessTokenApi.fetchPostRequest('fournisseur')
      .then(response => {
        stopLoadingSpinner();
        if (response?.api_access_token) {
          setAccessToken(response.api_access_token);
          setRole(response.api_role);
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  /*
* Method that handle the recuperation of the company full info
* */
  function getCompanyProfileInfo() {
    const requestData = {
      form: 'CSF_EP_UXCompany_REST',
      recordId: companyId
    };
    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
    pivotalFormApi.callFormRecuperationEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.primary) {
          setCompany(response.payload.data.primary);
        }
      })
      .catch(error => {
        console.error(error);
      });
  }

  function updateForm(localForm = {}) {
    if (Object.keys(localForm).length !== 0) {
      // form are the values initially obtained from the api.
      // localForm is the value saved when submitting a step.
      // We want to merge and override modified values during the step and push them to be visible in the interface.
      // Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
      // object spread {...form, ...localForm} somehow didn't work here.
      let newForm = {};
      Object.assign(newForm, form, localForm);
      setForm(newForm);
    } else {
      // refreshForm (bool) tells to fetch back from the api or not.
      setRefreshForm(true);
    }
  };

  function updateFormStep4() {
    setRefreshFormStep4(true);
  };

  function updateActivityId(trainingId) {
    setTrainingId(trainingId);
    setRefreshForm(true);
  }

  function updateActivityCycleIdStep4(activityId) {
    setActivityCycleId(activityId);
    setRefreshFormStep4(true);
  }

  function fetchFormStep4FromActivityCycleId() {

    const requestData = {
      form: 'CSF_UXMatiere_Info',
      recordId: activityCycleId
    };

    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
    return pivotalFormApi.callFormRecuperationEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.primary != null) {
          setFormStep4(response.payload?.data?.primary);
          stopLoadingSpinner();
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  function fetchFormFromRecordId() {
    const requestData = {
      form: 'CSF_EP_UXRecognition_Info',
      recordId: trainingId
    };
    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
    return pivotalFormApi.callFormRecuperationEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.primary != null) {
          setForm(response.payload?.data?.primary);

          // Renouvellement :
          // le renouvellement ne peut commencer qu'une journée après la fin de la formation initiale
          //   (ex : si fin 1re juillet 2028, début ne peut pas être avant le 2 juillet - 2028).
          // must keep original end date event after the dates are modified for the renewal
          const formData = response.payload?.data?.primary;

          if(formData && formData?.Cycle__Secondary && formData?.Cycle__Secondary[formData.Cycle__Secondary.length - 1] && formData?.Cycle__Secondary[formData.Cycle__Secondary.length - 1]?.End_Date !== null) {
            setOriginalEndDate(new moment(new Date(formData?.Cycle__Secondary[formData.Cycle__Secondary.length - 1]?.End_Date)).utc().format('YYYY-MM-DD'));
            setHasPreviousRenewalEndDate(formData.hasOwnProperty('Renew_Cycle_End_Date') && formData.Renew_Cycle_End_Date !== null && formData.Renew_Cycle_End_Date !== '');
          }

          setInitialLoadComplete(true);
          stopLoadingSpinner();
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  /*
  * Method to fetch form from the local storage if there is no trainingID defined
  */
  function fetchFormFromLocalStorage() {
    const formStep1 = localStorage.getItem('form');
    if (formStep1) {
      setForm(JSON.parse(formStep1));
    }
    stopLoadingSpinner();
  }

  /*
  * Method to fetch the connected user
  */
  function getConnectedUser() {
    const requestData = {
      search: 'Current_Ux_Client_User',
    };

    const pivotalSearchApi = new PivotalSearchApi(apiUrl, apiEnv, accessToken);
    pivotalSearchApi.callSearchEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.rows?.length >= 0) {
          setUser(response.payload.data.rows[0]);
          setCompanyId(response.payload.data.rows[0].Primary_Company);
          stopLoadingSpinner();
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  // Navigation buttons state
  const handleStepChange = (newStep) => {
    setCurrentStep(newStep);
  };

  /*
  * On form submit, send a post mesage to moneris to tokenize the credit card info
  */
  function OnSubmitPaymentForm() {
    startLoadingSpinner();
    let monerisFrameRef = document.getElementById('monerisFournisseurFrame').contentWindow;
    monerisFrameRef.postMessage('tokenize', monerisHostUrl + '/HPPtoken/index.php');
  }

  /**
   * Formate un nombre avec deux décimales pour le format de Moneris
   * Le nombre fournit à formatter peut être une string au format "1,234.78",
   * ou une string telle que "123.44", ou "1 234.55", ou "199,67",
   * ou un nombre,
   * et ce format texte n'est pas lié à la langue de la page.
   *
   * @param {number|string} number - Nombre à formater.
   * @returns {string} Nombre formaté en chaîne au format 9999.99
   */
  function formatAmount(number) {
    if (number === null || number === undefined) {
      // Moneris refuses amount of 0
      // https://developer.moneris.com/Documentation/NA/E-Commerce%20Solutions/Hosted%20Solutions/~/link.aspx?_id=8046F84336424CEEBB4FC24192CC6015&_z=z#preauthorizationwithvault
      return '0.00';
    }

    let str = String(number).trim();

    // Retirer les espaces et apostrophes souvent utilisés comme séparateurs de milliers
    str = str.replace(/[\s']/g, '');

    // Détection des séparateurs décimaux
    const hasComma = str.includes(',');
    const hasDot = str.includes('.');

    if (hasComma && hasDot) {
      // Si les deux sont présents, on suppose que le séparateur de milliers est la virgule
      // et le séparateur décimal est le point
      str = str.replace(/,/g, '');
    } else if (hasComma && !hasDot) {
      // Virgule seule : virgule décimale, on remplace la virgule par un point
      str = str.replace(/,/g, '.');
    }
    // Sinon, point seul ou aucun, on garde tel quel

    const num = Number(str);
    if (isNaN(num)) {
      // Moneris refuses amount of 0
      return '0.00';
    }

    return num.toFixed(2);
  }

  /*
  * Method that listen to the post message of moneris when the card is submitted
  */
  function respMsgMoneris(e) {
    if (e.data && e.origin === monerisHostUrl) {
      let respData = JSON.parse(e.data);
      if (typeof respData.responseCode !== 'undefined') {
        if (respData.responseCode.toString() === '001') {
          const token = respData.dataKey;
          let total = formatAmount(form.Montant_Total);
          if (total && typeof total === 'string') {
            total = parseFloat(total.replace(',', '.'));
          }
          const formData = {
            'activity': form.Name_French,
            'primary_contact_name': form.Contact_Id__Rn_Descriptor,
            'amount': total,
            'company': document.getElementById('company').value,
            'code': form.NoReconnaissance,
            'email': document.getElementById('email').value,
          }
          sendFormForPayment(token, formData);
        } else {
          setIsErrorMoneris(true);
          stopLoadingSpinner();
        }
      }
    }
  }

  /*
  * Method that send the info form to rhe backend to process the payment
  */
  function sendFormForPayment(token, formData) {
    const url = "/csf-api-fournisseur/training-payment-moneris";
    const requestMetadata = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pivotalEnvironmentName': apiEnv
      },
      body: JSON.stringify({
        token: token,
        formData: formData
      })
    };
    fetch(url, requestMetadata)
      .then(res => {
        if (!res.ok) {
          stopLoadingSpinner();
          setIsErrorMoneris(true);
        }
        return res;
      })
      .then(res => res.json())
      .then(data => {
        stopLoadingSpinner();
        if (data.success === true) {
          sendPivotalMonerisInfo(data.data);
          setCurrentStep(currentStep + 1);
        } else {
          setIsErrorMoneris(true);
        }
      }).catch((error) => {
        stopLoadingSpinner();
        setIsErrorMoneris(true);
      });
  }

  /*
  * Method to send to moneris order id to pivotal
  */
  function sendPivotalMonerisInfo(data) {
    const recogRecordId = form?.Last_Recognition_Id ? form?.Last_Recognition_Id : form?.Recognition_Id;

    const requestData = {
      form: 'CSF_EP_Moneris_REST',
      recordId: recogRecordId,
      deltaData: {
        Code_Moneris: data.transaction_number
      }
    };

    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
    pivotalFormApi.callFormPutEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
      })
      .catch(error => {
      });
  }

  return initialLoadComplete && (
    <div>
      {currentStep === 1 && (
        <div id="componentStep1"><FournisseurRecognitionRequestStepOneBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} trainingId={trainingId} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} user={user} form={form} updateForm={updateForm} updateActivityId={updateActivityId} urlHome={urlHome} urlRequestRecognition={urlRequestRecognition} originalEndDate={originalEndDate} firstTimeNewRecognition={fistTimeNewRecognition} setFirstTimeNewRecognition={setFistTimeNewRecognition} setContact={setContact} /></div>
      )}
      {currentStep === 2 && (
        <div id="componentStep2"><FournisseurRecognitionRequestStepTwoBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate}  trainingId={trainingId} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={form} updateForm={updateForm} updateActivityCycleIdStep4={updateActivityCycleIdStep4} /> </div>
      )}
      {currentStep === 3 && (
        <div id="componentStep3"><FournisseurRecognitionRequestStepThreeBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={form} updateForm={updateForm} /></div>
      )}
      {currentStep === 4 && (
        <div id="componentStep4"><FournisseurRecognitionRequestStepFourBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} role={role} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={formStep4} updateForm={updateFormStep4} originalForm={form} updateOriginalForm={updateForm} trainingId={trainingId} activityCycleId={activityCycleId} /></div>
      )}
      {currentStep === 5 && (
        <div id="componentStep4b"><FournisseurRecognitionRequestStepFourConfirmBlock currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={form} urlActivityDetail={urlActivityDetail} urlHome={urlHome} /></div>
      )}
      {currentStep === 6 && role !== 1 && (
        <div id="componentStep5"><FournisseurRecognitionRequestStepFiveBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} updateForm={updateForm} apiUrl={apiUrl} apiEnv={apiEnv} form={form} /></div>
      )}
      {currentStep === 7 && role !== 1 && (
        <div id="componentStep6"><FournisseurRecognitionRequestStepSixBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={form} user={user} updateForm={updateForm} contact={contact}/></div>
      )}
      {currentStep === 8 && (
        <div id="componentStepPayment"><FournisseurRecognitionRequestStepPaymentBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} company={company} user={user} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={form} monerisHostUrl={monerisHostUrl} monerisHostId={monerisHostId} OnSubmitPaymentForm={OnSubmitPaymentForm} isErrorMoneris={isErrorMoneris} /></div>
      )}
      {currentStep === 9 && (
        <div id="componentStepPayment"><FournisseurRecognitionRequestStepPaymentConfirmationBlock hasPreviousRenewalEndDate={hasPreviousRenewalEndDate} currentStep={currentStep} onStepChange={handleStepChange} accessToken={accessToken} apiUrl={apiUrl} apiEnv={apiEnv} form={form} urlActivityDetail={urlActivityDetail} urlHome={urlHome} /></div>
      )}
    </div>
  );
}

window.addEventListener('DOMContentLoaded', () => {
  const domNode = document.getElementById('FournisseurRecognitionRequestBlock');
  if (domNode !== null) {
    const root = createRoot(domNode);

    const apiUrl = domNode.getAttribute("data-api-url");
    const apiEnv = domNode.getAttribute("data-api-env");
    const urlActivityDetail = domNode.getAttribute("data-activity-detail-url");
    const urlHome = domNode.getAttribute("data-home-url");
    const urlRequestRecognition = domNode.getAttribute("data-url-request-recognition");

    const monerisHostUrl = domNode.getAttribute("data-moneris-url");
    const monerisHostId = domNode.getAttribute("data-moneris-id");

    root.render(<FournisseurRecognitionRequestBlock
      apiUrl={apiUrl}
      apiEnv={apiEnv}
      urlActivityDetail={urlActivityDetail}
      urlHome={urlHome}
      urlRequestRecognition={urlRequestRecognition}
      monerisHostUrl={monerisHostUrl}
      monerisHostId={monerisHostId}
    />);
  }
});

FournisseurRecognitionRequestBlock.propTypes = {
  apiUrl: PropTypes.string,
  apiEnv: PropTypes.string,
  urlActivityDetail: PropTypes.string,
  urlHome: PropTypes.string,
  urlRequestRecognition: PropTypes.string,
  monerisHostUrl: PropTypes.string,
  monerisHostId: PropTypes.string
};
